import clsx from 'clsx';
import { useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react';

import styles from './OtpInput.module.scss';

type OtpInputProps = {
  value: string[];
  onChange: (next: string[]) => void;
  /** Called on any user interaction — handy for clearing a parent error. */
  onUserInput?: () => void;
  length?: number;
  autoFocus?: boolean;
  className?: string;
};

const OtpInput = ({
  value,
  onChange,
  onUserInput,
  length = 6,
  autoFocus,
  className,
}: OtpInputProps) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const focusInput = (index: number) => {
    const input = inputsRef.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  // Fills the boxes from index on with the digits of text: a typed digit, or a whole code pasted or
  // filled in by the keyboard's one-time-code suggestion.
  const fill = (index: number, text: string) => {
    const digits = text.replace(/\D/g, '');
    const next = [...value];
    if (!digits) {
      next[index] = '';
      onChange(next);
      return;
    }
    digits.split('').forEach((char, i) => {
      if (index + i < length) next[index + i] = char;
    });
    onChange(next);
    focusInput(Math.min(index + digits.length, length - 1));
  };

  const handleChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const typed = e.target.value;
    // A box already holding a digit receives the old digit plus the new one: keep the new one.
    fill(index, typed.length === 2 && value[index] ? typed.slice(-1) : typed);
    onUserInput?.();
  };

  const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    onUserInput?.();

    if (e.key === 'Backspace') {
      if (value[index]) {
        e.preventDefault();
        const next = [...value];
        next[index] = '';
        onChange(next);
      } else if (index > 0) {
        // On an empty box, erase the previous digit right away rather than just selecting it,
        // so each Backspace removes one digit.
        e.preventDefault();
        const next = [...value];
        next[index - 1] = '';
        onChange(next);
        focusInput(index - 1);
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    }

    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (index: number) => (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    if (!pasted.replace(/\D/g, '')) return;

    fill(index, pasted);
    onUserInput?.();
  };

  return (
    <div className={clsx(styles.boxes, className)}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          // The first box takes the keyboard's one-time-code suggestion (SMS, RCS or email) whole.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={index === 0 ? length : 2}
          aria-label={`Digit ${index + 1} of ${length}`}
          autoFocus={autoFocus && index === 0}
          className={styles.box}
          value={value[index] ?? ''}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onFocus={() => focusInput(index)}
          onPaste={handlePaste(index)}
        />
      ))}
    </div>
  );
};

export default OtpInput;
